import { isReference } from '@apollo/client';
import type { FetchResult, StoreObject } from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { RoutineRecord } from '@models/routine';

import { parseArgs, type CachedRecordList } from '@utils/apollo';

import type {
  PostRoutineRecordMutationVariables,
  PostRoutineRecordMutation,
  UpdateRoutineRecordMutation,
  UpdateRoutineRecordMutationVariables,
  DeleteRoutineRecordMutation,
  DeleteRoutineRecordMutationVariables,
  GetRoutineRecordQueryVariables,
} from 'generated/routine.generated';
import {
  useGetRoutineRecordQuery,
  usePostRoutineRecordMutation,
  useGetRoutineQuery,
  useUpdateRoutineRecordMutation,
  useDeleteRoutineRecordMutation,
} from 'generated/routine.generated';

export const useGetRoutines = () => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetRoutineRecordQuery({
    variables: { input: { patient: patient_id, count: 1500, page: 1 } },
    skip: !patient_id,
  });
  const { data, error } = response;
  handleError(error);
  const records = (data?.listTasks.data ?? [])
    .filter((r) => r.input?.some((i) => Boolean(i.valueTiming?.repeat)))
    .map((r) => new RoutineRecord(r));
  return { ...response, data: records };
};

export const useGetRoutine = (params: { record_id: string }) => {
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetRoutineQuery({
    variables: { input: { ...params, patient_id } },
    skip: !patient_id,
  });
  const { data } = response;
  const record = data?.getTask;
  return { ...response, data: record ? new RoutineRecord(record) : null };
};

export function usePostRoutine(): [
  (
    params: PostRoutineRecordMutationVariables
  ) => Promise<FetchResult<PostRoutineRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = usePostRoutineRecordMutation();

  const loading = baseLoading;

  async function postRoutine(params: PostRoutineRecordMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.postTaskRecord) {
          return;
        }
        const storeObject = response.postTaskRecord as unknown as StoreObject;
        try {
          cache.modify<{ listTasks: CachedRecordList }>({
            fields: {
              listTasks: (existing, { storeFieldName, toReference }) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed =
                  parseArgs<GetRoutineRecordQueryVariables>(storeFieldName);
                if (parsed?.input.patient === params.input.for?.reference) {
                  return {
                    ...existing,
                    data: [...existing.data, toReference(storeObject)],
                  };
                }
                return existing;
              },
            },
          });
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }

  return [postRoutine, loading];
}

export function useUpdateRoutine(): [
  (
    params: UpdateRoutineRecordMutationVariables
  ) => Promise<FetchResult<UpdateRoutineRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useUpdateRoutineRecordMutation();

  const loading = baseLoading;

  async function updateRoutine(params: UpdateRoutineRecordMutationVariables) {
    const response = await mutate({
      variables: { ...params },
    });
    return response;
  }

  return [updateRoutine, loading];
}

export function useDeleteRoutine(): [
  (
    params: DeleteRoutineRecordMutationVariables
  ) => Promise<FetchResult<DeleteRoutineRecordMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useDeleteRoutineRecordMutation();

  const loading = baseLoading;

  async function deleteRoutine(params: DeleteRoutineRecordMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.deleteTaskRecord) {
          return;
        }
        try {
          const { task_id: id } = params.input;
          const normalizedId = cache.identify({
            id,
            __typename: 'TaskModel',
          });
          cache.evict({ id: normalizedId });
          cache.gc();
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    return response;
  }

  return [deleteRoutine, loading];
}
